
import React, { useRef } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAsteroidsEngine } from './hooks/useAsteroidsEngine';
import AsteroidsTouchControls from './AsteroidsTouchControls';
import AsteroidsInstructions from './AsteroidsInstructions';
import GameLoading from './components/GameLoading';
import GameError from './components/GameError';
import GameCanvas from './components/GameCanvas';
import MobileInstructions from '../common/MobileInstructions';
import { Skeleton } from '@/components/ui/skeleton';

interface AsteroidsGameProps {
  title: string;
}

const AsteroidsGame: React.FC<AsteroidsGameProps> = ({ title }) => {
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isMobile = useIsMobile();
  
  const {
    gameEngine,
    isLoading,
    error,
    updateCanvasSize,
    retryInitialization
  } = useAsteroidsEngine({
    canvasRef,
    containerRef: gameContainerRef
  });
  
  // Initialize canvas size on mount
  React.useEffect(() => {
    if (canvasRef.current && gameContainerRef.current) {
      updateCanvasSize();
    }
  }, []);

  // If there's an error, show the error component
  if (error) {
    return <GameError errorMessage={error} onRetry={retryInitialization} />;
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center relative">
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center">
          <Skeleton className="w-3/4 h-3/4" />
        </div>
      ) : (
        <>
          <GameCanvas
            canvasRef={canvasRef}
            containerRef={gameContainerRef}
            gameEngine={gameEngine}
            updateCanvasSize={updateCanvasSize}
          />
          
          <AsteroidsInstructions isMobile={isMobile} />
          {isMobile && (
            <>
              <AsteroidsTouchControls engine={gameEngine} />
              <MobileInstructions gameType="Asteroids" />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default AsteroidsGame;
